import { useAppSelector } from "features/store";
import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { canSeeIntegrationsPlatform, isDeveloper } from "utils/roles";

type PlatformRouteProps = {
  children: ReactNode;
  /** Restrict to developers (integration registration = lifecycle tier). */
  requireDeveloper?: boolean;
};

/**
 * Gate for the integration-platform UI (CAL-5832): any internal platform role
 * (developer / superuser / calibrate manager) may view; routes flagged
 * `requireDeveloper` (the register wizard) additionally need the developer
 * role. Mirrors the backend tiers in integration_platform/api/core/
 * permissions.py - the API enforces its own permissions regardless.
 */
const PlatformRoute = ({
  children,
  requireDeveloper = false,
}: PlatformRouteProps): JSX.Element => {
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const pp = user?.platform_profile;

  if (user && !user.is_pass_reset) {
    return <Navigate to="/reset-password" replace />;
  }

  const allowed = requireDeveloper
    ? isDeveloper(pp)
    : canSeeIntegrationsPlatform(pp);

  if (!user || !allowed) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default PlatformRoute;
